import { useState, useEffect } from "react";
import { Camera, Video, MapPin, CheckCircle, XCircle, Trash2, Eye, Clock, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import AdminLayout from "@/components/layout/AdminLayout";
import axios from "axios";
import { API } from "@/App";
import AdminPagination from "@/components/admin/AdminPagination";

const statusConfig = {
  pending: { label: "Pendiente", color: "bg-yellow-100 text-yellow-700", icon: Clock },
  approved: { label: "Aprobado", color: "bg-green-100 text-green-700", icon: CheckCircle },
  rejected: { label: "Rechazado", color: "bg-red-100 text-red-700", icon: XCircle },
  converted: { label: "Convertido", color: "bg-blue-100 text-blue-700", icon: CheckCircle },
};

export default function AdminReportes() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedReport, setSelectedReport] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [adminNotes, setAdminNotes] = useState("");
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchReports();
  }, [statusFilter, page, limit]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.append("status", statusFilter);
      params.append("skip", String((page - 1) * limit));
      params.append("limit", String(limit));
      
      const response = await axios.get(`${API}/admin/news-reports?${params}`);
      setReports(response.data.items || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error("Error al cargar reportes");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (reportId, status) => {
    setUpdating(true);
    try {
      const formData = new FormData();
      formData.append("status", status);
      formData.append("admin_notes", adminNotes);
      
      await axios.put(`${API}/admin/news-reports/${reportId}`, formData);
      toast.success(`Reporte ${status === "approved" ? "aprobado" : "rechazado"}`);
      setSelectedReport(null);
      setAdminNotes("");
      fetchReports();
    } catch (error) {
      toast.error("Error al actualizar");
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/admin/news-reports/${deleteId}`);
      toast.success("Reporte eliminado");
      setDeleteId(null);
      fetchReports();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const openPreview = (report) => {
    setSelectedReport(report);
    setAdminNotes(report.admin_notes || "");
  };

  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith("http")) return url;
    return `${API.replace("/api", "")}${url}`;
  };

  const pending = reports.filter(r => r.status === "pending").length;

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-reportes">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-brand-text">
              Reportes de Noticias
            </h1>
            <p className="text-brand-textSecondary mt-1">
              {pending > 0 && (
                <span className="text-amber-600 font-medium">{pending} pendiente(s) · </span>
              )}
              {reports.length} reportes en total
            </p>
          </div>
        </div>

        {/* Filter */}
        <div className="flex gap-4">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="pending">Pendientes</SelectItem>
              <SelectItem value="approved">Aprobados</SelectItem>
              <SelectItem value="rejected">Rechazados</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Reports List */}
        {loading ? (
          <div className="text-center py-12 text-gray-400">Cargando...</div>
        ) : reports.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border">
            <Camera className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No hay reportes</p>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {reports.map(report => {
              const sc = statusConfig[report.status] || statusConfig.pending;
              const StatusIcon = sc.icon;
              
              return (
                <div 
                  key={report.report_id} 
                  className="bg-white rounded-xl border overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => openPreview(report)}
                  data-testid={`report-${report.report_id}`}
                >
                  {/* Photos Preview */}
                  {report.photo_urls && report.photo_urls.length > 0 ? (
                    <div className="h-40 bg-gray-100 relative">
                      <img 
                        src={getImageUrl(report.photo_urls[0])} 
                        alt="" 
                        className="w-full h-full object-cover"
                      />
                      {report.photo_urls.length > 1 && (
                        <Badge className="absolute bottom-2 right-2 bg-black/50 text-white">
                          +{report.photo_urls.length - 1} fotos
                        </Badge>
                      )}
                      {report.video_url && (
                        <Badge className="absolute bottom-2 left-2 bg-red-500 text-white">
                          <Video className="h-3 w-3 mr-1" /> Video
                        </Badge>
                      )}
                    </div>
                  ) : (
                    <div className="h-32 bg-gray-100 flex items-center justify-center">
                      <Camera className="h-10 w-10 text-gray-300" />
                    </div>
                  )}

                  {/* Content */}
                  <div className="p-4">
                    <Badge className={`${sc.color} text-xs mb-2`}>
                      <StatusIcon className="h-3 w-3 mr-1" />
                      {sc.label}
                    </Badge>
                    
                    <p className="text-gray-900 line-clamp-2 mb-2">{report.description}</p>
                    
                    {report.location && (
                      <div className="flex items-center gap-1 text-xs text-gray-500 mb-2">
                        <MapPin className="h-3 w-3" />
                        <span className="truncate">{report.location}</span>
                      </div>
                    )}

                    <div className="flex items-center justify-between text-xs text-gray-400 pt-2 border-t">
                      <span>{new Date(report.created_at).toLocaleDateString("es-HN")}</span>
                      {report.contact_name && <span>{report.contact_name}</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Preview Dialog */}
        <Dialog open={!!selectedReport} onOpenChange={() => setSelectedReport(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Detalle del Reporte</DialogTitle>
            </DialogHeader>
            
            {selectedReport && (
              <div className="space-y-4">
                {/* Status */}
                <Badge className={`${statusConfig[selectedReport.status]?.color}`}>
                  {statusConfig[selectedReport.status]?.label}
                </Badge>

                {/* Photos */}
                {selectedReport.photo_urls && selectedReport.photo_urls.length > 0 && (
                  <div className="grid grid-cols-3 gap-2">
                    {selectedReport.photo_urls.map((url, i) => (
                      <a 
                        key={i} 
                        href={getImageUrl(url)} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="aspect-square bg-gray-100 rounded-lg overflow-hidden"
                      >
                        <img src={getImageUrl(url)} alt="" className="w-full h-full object-cover" />
                      </a>
                    ))}
                  </div>
                )}

                {/* Video */}
                {selectedReport.video_url && (
                  <video 
                    src={getImageUrl(selectedReport.video_url)} 
                    controls 
                    className="w-full rounded-lg"
                  />
                )}

                {/* Description */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-1">Descripción</h4>
                  <p className="text-gray-600 whitespace-pre-line">{selectedReport.description}</p>
                </div>

                {/* Location */}
                {(selectedReport.location || selectedReport.lat) && (
                  <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg">
                    <MapPin className="h-5 w-5 text-gray-500 mt-0.5" />
                    <div>
                      {selectedReport.location && <p className="text-gray-900">{selectedReport.location}</p>}
                      {selectedReport.lat && (
                        <a 
                          href={`https://maps.google.com/?q=${selectedReport.lat},${selectedReport.lng}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-emerald-600 hover:underline"
                        >
                          Ver en Google Maps ({selectedReport.lat.toFixed(4)}, {selectedReport.lng.toFixed(4)})
                        </a>
                      )}
                    </div>
                  </div>
                )}

                {/* Contact Info */}
                {(selectedReport.contact_name || selectedReport.contact_phone) && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-1">Contacto</h4>
                    {selectedReport.contact_name && <p className="text-gray-600">{selectedReport.contact_name}</p>}
                    {selectedReport.contact_phone && (
                      <a href={`tel:${selectedReport.contact_phone}`} className="text-emerald-600">
                        {selectedReport.contact_phone}
                      </a>
                    )}
                  </div>
                )}

                {/* Date */}
                <p className="text-sm text-gray-500">
                  Recibido: {new Date(selectedReport.created_at).toLocaleString("es-HN")}
                </p>

                {/* Admin Notes */}
                {selectedReport.status === "pending" && (
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                      Notas del administrador
                    </label>
                    <Textarea
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      placeholder="Agregar notas (opcional)..."
                      rows={2}
                    />
                  </div>
                )}

                {/* Previous Notes */}
                {selectedReport.admin_notes && selectedReport.status !== "pending" && (
                  <div className="p-3 bg-amber-50 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-1">Notas</h4>
                    <p className="text-gray-600">{selectedReport.admin_notes}</p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex flex-wrap gap-2 pt-4 border-t">
                  {selectedReport.status === "pending" && (
                    <>
                      <Button 
                        onClick={() => handleUpdateStatus(selectedReport.report_id, "approved")}
                        className="bg-green-600 hover:bg-green-700"
                        disabled={updating}
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Aprobar
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => handleUpdateStatus(selectedReport.report_id, "rejected")}
                        disabled={updating}
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        Rechazar
                      </Button>
                    </>
                  )}
                  <Button 
                    variant="outline" 
                    className="text-red-500 ml-auto"
                    onClick={() => { setSelectedReport(null); setDeleteId(selectedReport.report_id); }}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Eliminar
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Eliminar reporte?</AlertDialogTitle>
              <AlertDialogDescription>
                Esta acción no se puede deshacer.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
                Eliminar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
        <AdminPagination
          page={page}
          total={total}
          limit={limit}
          onPageChange={setPage}
          onLimitChange={(l) => { setLimit(l); setPage(1); }}
          testId="reportes-pagination"
          label="reportes"
        />
    </AdminLayout>
  );
}
